import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import Api from '../Config/ApiConfig';

/**
 * Check if passkeys are supported in the current browser
 */
export const isPasskeySupported = () => {
  return window.PublicKeyCredential && 
         typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function';
};

/**
 * Check if platform authenticator is available
 */
export const isPlatformAuthenticatorAvailable = async () => {
  if (!isPasskeySupported()) return false;
  
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (error) {
    console.error('Error checking platform authenticator availability:', error);
    return false;
  }
};

/**
 * Register a new passkey for the current admin
 */
export const registerPasskey = async (deviceName = 'Unknown Device') => {
  try {
    // Check if passkeys are supported
    if (!isPasskeySupported()) {
      throw new Error('Passkeys are not supported in this browser');
    }

    // Get registration options from server
    const optionsResponse = await Api.post('/passkey/register/generate-options');
    const options = optionsResponse.data.data;

    // Start registration process
    const registrationResponse = await startRegistration(options);

    // Send registration response to server for verification
    const verificationResponse = await Api.post('/passkey/register/verify', {
      response: registrationResponse,
      deviceName
    });

    return verificationResponse.data;
  } catch (error) {
    console.error('Passkey registration error:', error);
    throw error;
  }
};

/**
 * Authenticate using passkey
 */
export const authenticateWithPasskey = async (email) => {
  try {
    // Check if passkeys are supported
    if (!isPasskeySupported()) {
      throw new Error('Passkeys are not supported in this browser');
    }

    // Get authentication options from server
    const optionsResponse = await Api.post('/passkey/auth/generate-options', { email });
    const options = optionsResponse.data.data;

    // Start authentication process
    const authenticationResponse = await startAuthentication(options);

    // Send authentication response to server for verification
    const verificationResponse = await Api.post('/passkey/auth/verify', {
      email,
      response: authenticationResponse
    });

    return verificationResponse.data;
  } catch (error) {
    console.error('Passkey authentication error:', error);
    throw error;
  }
};

/**
 * Get list of registered passkeys for current admin
 */
export const getPasskeys = async () => {
  try {
    const response = await Api.get('/passkey/list');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching passkeys:', error);
    throw error;
  }
};

/**
 * Remove a passkey
 */
export const removePasskey = async (credentialId) => {
  try {
    const response = await Api.delete(`/passkey/${credentialId}`);
    return response.data;
  } catch (error) {
    console.error('Error removing passkey:', error);
    throw error;
  }
};

/**
 * Check if user has passkeys enabled
 */
export const checkPasskeyAvailability = async (email) => {
  try {
    console.log('Making API call to check passkey status for:', email);
    const response = await Api.post('/passkey/check-status', { email });
    console.log('API response:', response.data);
    return response.data.data.passkeyEnabled;
  } catch (error) {
    console.error('Error checking passkey availability:', error);
    return false;
  }
};